import type { AcriAnswer } from '@/components/OnboardingQuestionPage'
import OnboardingQuestionPage from '@/components/OnboardingQuestionPage'
import { getAnswer } from '@/utils/onboardingStorage'
import { useTranslation } from 'react-i18next'

type TaskSet = { label: string; tasks: AcriAnswer[] }

const ROLE_TASKS: Record<string, TaskSet> = {
  'marketing-manager': {
    label: 'Marketing Manager',
    tasks: [
      {
        title: 'Campaign planning & briefing',
        subtitle:
          'Defining campaign objectives, timelines, and creative briefs',
        value: 'campaign-planning',
      },
      {
        title: 'Budget allocation & tracking',
        subtitle: 'Distributing spend across channels and monitoring ROI',
        value: 'budget-tracking',
      },
      {
        title: 'Performance reporting',
        subtitle:
          'Pulling cross-channel metrics and building weekly/monthly reports',
        value: 'performance-reporting',
      },
      {
        title: 'Content calendar management',
        subtitle: 'Planning and scheduling content across channels and formats',
        value: 'content-calendar',
      },
      {
        title: 'Vendor & agency coordination',
        subtitle:
          'Briefing agencies, reviewing deliverables, managing timelines',
        value: 'vendor-coordination',
      },
      {
        title: 'Market research & competitive analysis',
        subtitle:
          'Monitoring competitors, industry trends, and customer segments',
        value: 'market-research',
      },
      {
        title: 'A/B test design & analysis',
        subtitle:
          'Setting up experiments for subject lines, landing pages, and ads',
        value: 'ab-testing',
      },
      {
        title: 'Brand messaging & positioning',
        subtitle: 'Crafting how the brand sounds and what it stands for',
        value: 'brand-messaging',
      },
      {
        title: 'Cross-functional alignment',
        subtitle:
          'Coordinating with sales, product, and leadership on go-to-market',
        value: 'cross-functional',
      },
    ],
  },
  'digital-marketing-specialist': {
    label: 'Digital Marketing Specialist',
    tasks: [
      {
        title: 'Paid media management',
        subtitle: 'Running and optimizing ads across Google, Meta, LinkedIn',
        value: 'paid-media',
      },
      {
        title: 'SEO optimization',
        subtitle: 'Keyword research, on-page optimization, technical audits',
        value: 'seo',
      },
      {
        title: 'Email campaign execution',
        subtitle: 'Building, segmenting, and sending email campaigns',
        value: 'email-campaigns',
      },
      {
        title: 'Analytics & reporting',
        subtitle: 'Tracking KPIs across channels and building dashboards',
        value: 'analytics',
      },
      {
        title: 'Landing page creation',
        subtitle: 'Building and optimizing conversion-focused pages',
        value: 'landing-pages',
      },
      {
        title: 'Social media scheduling',
        subtitle: 'Planning and publishing content across platforms',
        value: 'social-scheduling',
      },
    ],
  },
  'software-engineer': {
    label: 'Software Engineer',
    tasks: [
      {
        title: 'Writing & reviewing code',
        subtitle: 'Building features, fixing bugs, reviewing pull requests',
        value: 'coding',
      },
      {
        title: 'System design & architecture',
        subtitle: 'Designing scalable systems and technical solutions',
        value: 'system-design',
      },
      {
        title: 'Debugging & troubleshooting',
        subtitle: 'Diagnosing issues, reading logs, fixing production problems',
        value: 'debugging',
      },
      {
        title: 'Code review & mentoring',
        subtitle: 'Reviewing PRs, giving feedback, helping junior devs',
        value: 'code-review',
      },
      {
        title: 'Testing & QA',
        subtitle: 'Writing unit tests, integration tests, ensuring quality',
        value: 'testing',
      },
      {
        title: 'Documentation',
        subtitle: 'Writing technical docs, API specs, runbooks',
        value: 'documentation',
      },
      {
        title: 'Sprint planning & standups',
        subtitle: 'Estimating work, updating tickets, team coordination',
        value: 'sprint-planning',
      },
      {
        title: 'DevOps & deployments',
        subtitle: 'CI/CD pipelines, infrastructure, monitoring',
        value: 'devops',
      },
    ],
  },
  'product-manager': {
    label: 'Product Manager',
    tasks: [
      {
        title: 'Roadmap planning',
        subtitle:
          'Defining priorities, sequencing features, stakeholder alignment',
        value: 'roadmap',
      },
      {
        title: 'User research & interviews',
        subtitle: 'Talking to users, analyzing feedback, identifying needs',
        value: 'user-research',
      },
      {
        title: 'Writing specs & requirements',
        subtitle: 'PRDs, user stories, acceptance criteria',
        value: 'specs',
      },
      {
        title: 'Data analysis & metrics',
        subtitle:
          'Analyzing product metrics, running experiments, tracking KPIs',
        value: 'data-analysis',
      },
      {
        title: 'Cross-functional coordination',
        subtitle: 'Aligning engineering, design, marketing, and sales',
        value: 'cross-functional',
      },
      {
        title: 'Competitive analysis',
        subtitle: 'Monitoring competitors, market trends, positioning',
        value: 'competitive-analysis',
      },
      {
        title: 'Stakeholder presentations',
        subtitle: 'Business reviews, exec updates, board presentations',
        value: 'presentations',
      },
    ],
  },
  'financial-analyst': {
    label: 'Financial Analyst',
    tasks: [
      {
        title: 'Financial modeling',
        subtitle: 'Building and maintaining forecasting models',
        value: 'financial-modeling',
      },
      {
        title: 'Variance analysis',
        subtitle: 'Comparing actuals vs budget and explaining differences',
        value: 'variance-analysis',
      },
      {
        title: 'Report preparation',
        subtitle: 'Monthly/quarterly financial reports and dashboards',
        value: 'report-prep',
      },
      {
        title: 'Data gathering & reconciliation',
        subtitle: 'Pulling data from multiple systems, ensuring accuracy',
        value: 'data-gathering',
      },
      {
        title: 'Budget planning',
        subtitle: 'Annual budgets, departmental allocations, scenario planning',
        value: 'budget-planning',
      },
      {
        title: 'Investment analysis',
        subtitle: 'Evaluating ROI on projects, initiatives, and spend',
        value: 'investment-analysis',
      },
    ],
  },
  'sales-manager': {
    label: 'Sales Manager',
    tasks: [
      {
        title: 'Pipeline management',
        subtitle: 'Tracking deals, forecasting revenue, managing CRM',
        value: 'pipeline',
      },
      {
        title: 'Team coaching & 1:1s',
        subtitle: 'Coaching reps, reviewing calls, performance feedback',
        value: 'coaching',
      },
      {
        title: 'Quota setting & territory planning',
        subtitle: 'Defining targets, assigning accounts, balancing workload',
        value: 'quota-planning',
      },
      {
        title: 'Client meetings & negotiations',
        subtitle: 'Strategic calls, contract discussions, closing deals',
        value: 'client-meetings',
      },
      {
        title: 'Sales reporting',
        subtitle: 'Weekly forecasts, win/loss analysis, board updates',
        value: 'sales-reporting',
      },
      {
        title: 'Cross-functional coordination',
        subtitle: 'Working with marketing, product, and customer success',
        value: 'cross-functional',
      },
    ],
  },
  'hr-manager': {
    label: 'HR Manager',
    tasks: [
      {
        title: 'Recruitment & hiring',
        subtitle: 'Job postings, screening, interviews, offer management',
        value: 'recruitment',
      },
      {
        title: 'Employee relations',
        subtitle: 'Handling concerns, conflict resolution, policy questions',
        value: 'employee-relations',
      },
      {
        title: 'Performance management',
        subtitle: 'Review cycles, goal setting, performance improvement plans',
        value: 'performance-mgmt',
      },
      {
        title: 'Policy & compliance',
        subtitle: 'Updating handbooks, ensuring legal compliance, audits',
        value: 'policy-compliance',
      },
      {
        title: 'Onboarding & offboarding',
        subtitle: 'New hire setup, orientation, exit interviews',
        value: 'onboarding',
      },
      {
        title: 'Compensation & benefits',
        subtitle: 'Salary benchmarking, benefits administration, surveys',
        value: 'comp-benefits',
      },
    ],
  },
  'data-scientist': {
    label: 'Data Scientist',
    tasks: [
      {
        title: 'Model building & training',
        subtitle: 'Developing ML models, feature engineering, tuning',
        value: 'model-building',
      },
      {
        title: 'Data exploration & cleaning',
        subtitle: 'EDA, data wrangling, handling missing values',
        value: 'data-cleaning',
      },
      {
        title: 'Statistical analysis',
        subtitle: 'Hypothesis testing, A/B test analysis, significance checks',
        value: 'statistical-analysis',
      },
      {
        title: 'Stakeholder presentations',
        subtitle: 'Translating findings into business recommendations',
        value: 'presentations',
      },
      {
        title: 'Pipeline & deployment',
        subtitle: 'Productionizing models, monitoring, retraining',
        value: 'deployment',
      },
      {
        title: 'Research & experimentation',
        subtitle: 'Reading papers, testing new approaches, prototyping',
        value: 'research',
      },
    ],
  },
  'operations-manager': {
    label: 'Operations Manager',
    tasks: [
      {
        title: 'Process optimization',
        subtitle: 'Identifying bottlenecks, streamlining workflows',
        value: 'process-optimization',
      },
      {
        title: 'Vendor management',
        subtitle: 'Negotiating contracts, managing supplier relationships',
        value: 'vendor-management',
      },
      {
        title: 'Resource planning',
        subtitle: 'Staffing, capacity planning, scheduling',
        value: 'resource-planning',
      },
      {
        title: 'Reporting & dashboards',
        subtitle: 'Operational KPIs, efficiency metrics, status updates',
        value: 'reporting',
      },
      {
        title: 'Cross-team coordination',
        subtitle: 'Aligning departments, managing dependencies',
        value: 'coordination',
      },
      {
        title: 'Budget oversight',
        subtitle: 'Cost tracking, purchase approvals, financial reviews',
        value: 'budget-oversight',
      },
    ],
  },
  'product-designer': {
    label: 'Product Designer',
    tasks: [
      {
        title: 'User research & testing',
        subtitle: 'Interviews, usability tests, survey analysis',
        value: 'user-research',
      },
      {
        title: 'Wireframing & prototyping',
        subtitle: 'Low and high-fidelity mockups, interactive prototypes',
        value: 'wireframing',
      },
      {
        title: 'Visual design',
        subtitle: 'UI design, design systems, component libraries',
        value: 'visual-design',
      },
      {
        title: 'Design reviews & critiques',
        subtitle: 'Presenting work, giving feedback, iterating on designs',
        value: 'design-reviews',
      },
      {
        title: 'Developer handoff',
        subtitle: 'Specs, redlines, answering implementation questions',
        value: 'dev-handoff',
      },
      {
        title: 'Stakeholder alignment',
        subtitle: 'Presenting rationale, getting buy-in, managing feedback',
        value: 'stakeholder-alignment',
      },
    ],
  },
  'account-executive': {
    label: 'Account Executive',
    tasks: [
      {
        title: 'Prospecting & outreach',
        subtitle: 'Cold emails, calls, LinkedIn, building pipeline',
        value: 'prospecting',
      },
      {
        title: 'Discovery calls',
        subtitle: 'Understanding pain points, qualifying opportunities',
        value: 'discovery',
      },
      {
        title: 'Demo & presentations',
        subtitle: 'Product walkthroughs, tailored pitch decks',
        value: 'demos',
      },
      {
        title: 'Proposal & contract work',
        subtitle: 'Writing proposals, negotiating terms, closing deals',
        value: 'proposals',
      },
      {
        title: 'CRM management',
        subtitle: 'Updating Salesforce/HubSpot, logging activities',
        value: 'crm',
      },
      {
        title: 'Relationship building',
        subtitle: 'Follow-ups, check-ins, expanding accounts',
        value: 'relationship-building',
      },
    ],
  },
  'frontend-developer': {
    label: 'Frontend Developer',
    tasks: [
      {
        title: 'UI implementation',
        subtitle: 'Building components, pages, and interactive features',
        value: 'ui-implementation',
      },
      {
        title: 'CSS & styling',
        subtitle: 'Responsive layouts, design system, animations',
        value: 'css-styling',
      },
      {
        title: 'API integration',
        subtitle: 'Connecting frontend to backend services and APIs',
        value: 'api-integration',
      },
      {
        title: 'Performance optimization',
        subtitle: 'Bundle size, rendering speed, Core Web Vitals',
        value: 'performance',
      },
      {
        title: 'Testing & debugging',
        subtitle: 'Unit tests, browser testing, fixing visual bugs',
        value: 'testing',
      },
      {
        title: 'Code review & collaboration',
        subtitle: 'Reviewing PRs, pair programming, design handoff',
        value: 'code-review',
      },
    ],
  },
  recruiter: {
    label: 'Recruiter',
    tasks: [
      {
        title: 'Sourcing candidates',
        subtitle: 'LinkedIn, job boards, referrals, outreach campaigns',
        value: 'sourcing',
      },
      {
        title: 'Screening & interviews',
        subtitle: 'Phone screens, initial assessments, scheduling',
        value: 'screening',
      },
      {
        title: 'Candidate pipeline management',
        subtitle: 'ATS updates, tracking progress, follow-ups',
        value: 'pipeline',
      },
      {
        title: 'Job description writing',
        subtitle: 'Crafting postings, requirements, employer branding',
        value: 'job-descriptions',
      },
      {
        title: 'Hiring manager coordination',
        subtitle: 'Intake meetings, feedback loops, offer approvals',
        value: 'hiring-manager',
      },
      {
        title: 'Offer negotiation & closing',
        subtitle: 'Extending offers, negotiating comp, closing candidates',
        value: 'offer-closing',
      },
    ],
  },
  'data-analyst': {
    label: 'Data Analyst',
    tasks: [
      {
        title: 'SQL queries & data extraction',
        subtitle: 'Writing queries, pulling data from databases',
        value: 'sql-queries',
      },
      {
        title: 'Dashboard building',
        subtitle: 'Creating visualizations in Tableau, Looker, Power BI',
        value: 'dashboards',
      },
      {
        title: 'Ad-hoc analysis',
        subtitle: 'Answering business questions with data deep-dives',
        value: 'ad-hoc',
      },
      {
        title: 'Data cleaning & validation',
        subtitle: 'Ensuring accuracy, handling missing values, QA',
        value: 'data-cleaning',
      },
      {
        title: 'Stakeholder reporting',
        subtitle: 'Weekly/monthly reports, presenting findings',
        value: 'reporting',
      },
      {
        title: 'Metric definition',
        subtitle: 'Defining KPIs, building tracking frameworks',
        value: 'metric-definition',
      },
    ],
  },
  'project-manager': {
    label: 'Project Manager',
    tasks: [
      {
        title: 'Project planning',
        subtitle: 'Timelines, milestones, resource allocation, Gantt charts',
        value: 'project-planning',
      },
      {
        title: 'Status tracking & reporting',
        subtitle: 'Progress updates, risk logs, stakeholder reports',
        value: 'status-tracking',
      },
      {
        title: 'Meeting facilitation',
        subtitle: 'Standups, retrospectives, steering committees',
        value: 'meeting-facilitation',
      },
      {
        title: 'Risk & issue management',
        subtitle: 'Identifying blockers, escalating, mitigation plans',
        value: 'risk-management',
      },
      {
        title: 'Cross-team coordination',
        subtitle: 'Aligning dependencies across teams and departments',
        value: 'cross-team',
      },
      {
        title: 'Documentation & process',
        subtitle: 'Project charters, process docs, lessons learned',
        value: 'documentation',
      },
    ],
  },
  'customer-success-manager': {
    label: 'Customer Success Manager',
    tasks: [
      {
        title: 'Onboarding new customers',
        subtitle: 'Implementation, training, first-value milestones',
        value: 'onboarding',
      },
      {
        title: 'Health monitoring',
        subtitle: 'Usage analytics, NPS tracking, churn risk assessment',
        value: 'health-monitoring',
      },
      {
        title: 'Quarterly business reviews',
        subtitle: 'Presenting ROI, planning next steps, expansion',
        value: 'qbrs',
      },
      {
        title: 'Support escalation',
        subtitle: 'Triaging issues, coordinating with product/engineering',
        value: 'support-escalation',
      },
      {
        title: 'Renewal management',
        subtitle: 'Contract renewals, upsell conversations, pricing',
        value: 'renewals',
      },
      {
        title: 'Relationship management',
        subtitle: 'Regular check-ins, building champion relationships',
        value: 'relationship-mgmt',
      },
    ],
  },
  'content-marketing-manager': {
    label: 'Content Marketing Manager',
    tasks: [
      {
        title: 'Content strategy',
        subtitle: 'Editorial calendar, content pillars, audience targeting',
        value: 'content-strategy',
      },
      {
        title: 'Blog & article writing',
        subtitle: 'Long-form content, thought leadership, SEO articles',
        value: 'writing',
      },
      {
        title: 'Social media content',
        subtitle: 'Posts, threads, short-form content across platforms',
        value: 'social-content',
      },
      {
        title: 'Email newsletter creation',
        subtitle: 'Drip campaigns, newsletters, nurture sequences',
        value: 'email-content',
      },
      {
        title: 'Performance analysis',
        subtitle: 'Traffic, engagement, conversion tracking per piece',
        value: 'content-analytics',
      },
      {
        title: 'Agency & freelancer management',
        subtitle: 'Briefing writers, reviewing drafts, quality control',
        value: 'agency-mgmt',
      },
    ],
  },
  'legal-counsel': {
    label: 'Legal Counsel',
    tasks: [
      {
        title: 'Contract review & drafting',
        subtitle: 'NDAs, MSAs, vendor agreements, amendments',
        value: 'contract-review',
      },
      {
        title: 'Legal research',
        subtitle: 'Case law, regulatory updates, compliance analysis',
        value: 'legal-research',
      },
      {
        title: 'Risk assessment',
        subtitle: 'Evaluating legal exposure, advising on decisions',
        value: 'risk-assessment',
      },
      {
        title: 'Compliance monitoring',
        subtitle: 'Regulatory filings, policy updates, audits',
        value: 'compliance',
      },
      {
        title: 'Litigation management',
        subtitle: 'Discovery, filings, working with outside counsel',
        value: 'litigation',
      },
      {
        title: 'Stakeholder advising',
        subtitle: 'Guiding business leaders on legal implications',
        value: 'advising',
      },
    ],
  },
}

const DEFAULT_TASKS: AcriAnswer[] = [
  {
    title: 'Writing & communication',
    subtitle: 'Emails, reports, presentations, documentation',
    value: 'writing',
  },
  {
    title: 'Data analysis & reporting',
    subtitle: 'Spreadsheets, dashboards, interpreting numbers',
    value: 'data-analysis',
  },
  {
    title: 'Meetings & coordination',
    subtitle: 'Calls, standups, cross-team alignment',
    value: 'meetings',
  },
  {
    title: 'Strategy & planning',
    subtitle: 'Goal setting, roadmaps, prioritization',
    value: 'strategy',
  },
  {
    title: 'Research & learning',
    subtitle: 'Industry trends, competitor analysis, upskilling',
    value: 'research',
  },
  {
    title: 'People management',
    subtitle: 'Coaching, 1:1s, performance reviews',
    value: 'people-mgmt',
  },
  {
    title: 'Process & admin',
    subtitle: 'Approvals, scheduling, operational tasks',
    value: 'process-admin',
  },
  {
    title: 'Client-facing work',
    subtitle: 'Calls, presentations, relationship management',
    value: 'client-facing',
  },
]

function getRoleLabel(roleValue: string): string {
  if (ROLE_TASKS[roleValue]) return ROLE_TASKS[roleValue].label
  return roleValue
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export default function DailyTasksPage() {
  const { t } = useTranslation()
  const roleAnswer = getAnswer('role')
  const roleValue = roleAnswer?.answer_key || ''
  const taskSet = ROLE_TASKS[roleValue]
  const tasks = taskSet?.tasks || DEFAULT_TASKS
  const roleLabel = getRoleLabel(roleValue)

  return (
    <OnboardingQuestionPage
      answers={tasks}
      multiple
      title="pages.onboarding.dailyTasks.title"
      subtitle={t('pages.onboarding.dailyTasks.subtitleTemplate', {
        defaultValue:
          'Standard task list for {{role}}. Select your regular work.',
        role: roleLabel,
      })}
    />
  )
}
